import React, { useState, useEffect, useCallback } from 'react';
import { useOnboardingTour, TourStepId } from '../../hooks/useOnboardingTour';
import DemoReader from './DemoReader';
import AchievementDisplay from './AchievementDisplay';
import { X, ArrowRight, ArrowLeft, Zap, Gauge, Play, Check } from 'lucide-react';

interface OnboardingTourProps {
  onComplete?: () => void;
  onSkip?: () => void;
  onSignup?: () => void;
}

const CHALLENGE_TEXT = `The future of reading is here. Speed reading technology has advanced rapidly in recent years, allowing people to consume information at unprecedented rates. With practice, anyone can learn to read at 600 words per minute or faster. The key is eliminating subvocalization - the habit of "saying" words in your head as you read them. RSVP (Rapid Serial Visual Presentation) training helps train your brain to process words directly without this internal voice. Start your journey today.`;

const OnboardingTour: React.FC<OnboardingTourProps> = ({ 
  onComplete, 
  onSkip,
  onSignup 
}) => {
  const {
    state,
    startTour,
    nextStep,
    prevStep,
    skipTour,
    getCurrentStep,
    getStepIndex,
    totalSteps
  } = useOnboardingTour();

  const [demoWpm, setDemoWpm] = useState(200);
  const [showChallenge, setShowChallenge] = useState(false);
  const [challengeComplete, setChallengeComplete] = useState(false);
  const [demoCompleted, setDemoCompleted] = useState(false);
  const [demoCompleteTime, setDemoCompleteTime] = useState<number | null>(null);
  const [speedExplanationAnimated, setSpeedExplanationAnimated] = useState(false);
  const [progressTrackingProgress, setProgressTrackingProgress] = useState(0);
  const [challengeReady, setChallengeReady] = useState(false);
  const [countdownWord, setCountdownWord] = useState<'getReady' | 'set' | 'go' | null>(null);
  const [countdownComplete, setCountdownComplete] = useState(false);
  
  // Track reading stats for progress screen
  const [readingStats, setReadingStats] = useState({
    demoWordsRead: 0,
    demoTimeMs: 0,
    rampWordsRead: 0,
    rampTimeMs: 0,
    maxSpeedReached: 200,
    challengeWordsRead: 0
  });

  // Smart auto-advance: when demo completes, wait 1s then advance
  useEffect(() => {
    if (!state.isActive || !demoCompleted || demoCompleteTime === null) return;
    
    const timeSinceComplete = Date.now() - demoCompleteTime;
    if (timeSinceComplete >= 1000) {
      // 1 second has passed since demo completed, advance now
      setDemoCompleted(false);
      setDemoCompleteTime(null);
      nextStep();
      return;
    }
    
    const remainingTime = 1000 - timeSinceComplete;
    const timer = setTimeout(() => {
      setDemoCompleted(false);
      setDemoCompleteTime(null);
      nextStep();
    }, remainingTime);
    
    return () => clearTimeout(timer);
  }, [state.isActive, demoCompleted, demoCompleteTime, nextStep]);

  // Auto-advance through steps based on duration (only for non-interactive steps)
  useEffect(() => {
    if (!state.isActive) return;
    
    // Don't auto-advance for steps that need user interaction
    if (state.currentStep === 'demo' || state.currentStep === 'speed-ramp' || state.currentStep === 'challenge' || state.currentStep === 'conversion' || state.currentStep === 'progress-tracking' || state.currentStep === 'summary' || state.currentStep === 'speed-explanation') return;

    const step = getCurrentStep();
    if (!step) return;

    const timer = setTimeout(() => {
      nextStep();
    }, step.duration);

    return () => clearTimeout(timer);
  }, [state.currentStep, state.isActive, getCurrentStep, nextStep]);

  // Demo WPM progression for speed ramp step - auto-ramps from 300 to 600 over 15 seconds
  useEffect(() => {
    if (state.currentStep === 'speed-ramp') {
      // Start the Ready/Set/Go countdown
      setCountdownWord('getReady');
      setCountdownComplete(false);
      
      // Get Ready for 1s, then Get Set for 0.5s, then Go for 0.5s
      const getReadyTimer = setTimeout(() => {
        setCountdownWord('set');
        
        const setTimer = setTimeout(() => {
          setCountdownWord('go');
          
          const goTimer = setTimeout(() => {
            setCountdownWord(null);
            setCountdownComplete(true);
          }, 500); // Go for 0.5s
          
          return () => clearTimeout(goTimer);
        }, 500); // Set for 0.5s
        
        return () => clearTimeout(setTimer);
      }, 1000); // Get Ready for 1s
      
      return () => {
        clearTimeout(getReadyTimer);
      };
    } else {
      // Reset countdown state when leaving the step
      setCountdownWord(null);
      setCountdownComplete(false);
    }
  }, [state.currentStep]);

  // Speed ramp WPM progression - runs after countdown completes
  useEffect(() => {
    if (state.currentStep === 'speed-ramp' && countdownComplete) {
      const interval = setInterval(() => {
        setDemoWpm(prev => {
          if (prev >= 600) return 600;
          const newWpm = prev + 25; // 300 -> 325 -> 350 ... -> 600 over 15 seconds (12 increments)
          
          // Track max speed reached
          if (newWpm > readingStats.maxSpeedReached) {
            setReadingStats(prev => ({ ...prev, maxSpeedReached: newWpm }));
          }
          
          return newWpm;
        });
      }, 1250); // 1250ms * 12 = 15 seconds
      return () => clearInterval(interval);
    }
  }, [state.currentStep, countdownComplete, readingStats.maxSpeedReached]);

  const handleChallengeComplete = useCallback(() => {
    setChallengeComplete(true);
    
    // Track challenge stats - CHALLENGE_TEXT word count
    const challengeWordCount = CHALLENGE_TEXT.split(/\s+/).filter(w => w.length > 0).length;
    
    setReadingStats(prev => ({
      ...prev,
      challengeWordsRead: challengeWordCount
    }));
  }, []);

  // Auto-advance after challenge completion to summary step (3 second delay)
  useEffect(() => {
    if (challengeComplete && state.currentStep === 'challenge') {
      const timer = setTimeout(() => {
        nextStep(); // Goes to summary step
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [challengeComplete, state.currentStep, nextStep]);

  const handleNext = useCallback(() => {
    const currentIndex = getStepIndex();
    if (currentIndex >= totalSteps - 1) {
      onComplete?.();
    } else {
      nextStep();
    }
  }, [getStepIndex, totalSteps, nextStep, onComplete]);

  const handleSkip = useCallback(() => {
    skipTour();
    onSkip?.();
  }, [skipTour, onSkip]);

  const handleDemoComplete = useCallback((wpm: number = 300) => {
    setDemoCompleted(true);
    setDemoCompleteTime(Date.now());
    
    // Track demo stats - 16 words in demo text
    const demoWordCount = 16;
    const timeMs = (demoWordCount / wpm) * 60 * 1000;
    
    setReadingStats(prev => ({
      ...prev,
      demoWordsRead: demoWordCount,
      demoTimeMs: timeMs
    }));
  }, []);

  // Reset challenge state when entering challenge step (don't auto-start)
  useEffect(() => {
    if (state.currentStep === 'challenge') {
      setShowChallenge(false);
      setChallengeComplete(false);
    }
  }, [state.currentStep]);

  // Animate speed explanation values when entering the step
  useEffect(() => {
    if (state.currentStep === 'speed-explanation' && !speedExplanationAnimated) {
      const timer = setTimeout(() => {
        setSpeedExplanationAnimated(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [state.currentStep, speedExplanationAnimated]);

  // Animate progress tracking bar when entering that step
  useEffect(() => {
    if (state.currentStep === 'progress-tracking') {
      const interval = setInterval(() => {
        setProgressTrackingProgress(prev => {
          if (prev >= 66) return 66; // 2 of 3 steps complete
          return prev + 7;
        });
      }, 600);
      return () => clearInterval(interval);
    } else {
      // Reset progress when leaving the step
      setProgressTrackingProgress(0);
    }
  }, [state.currentStep]);

  // Don't render if not active (except welcome step which shows first)
  if (!state.isActive && state.currentStep !== 'welcome') return null;

  // Render welcome overlay (shows first, before tour starts)
  if (state.currentStep === 'welcome') {
    return (
      <div className="fixed inset-0 bg-white dark:bg-slate-950 flex items-center justify-center z-50 animate-fadeIn">
        <div className="absolute top-4 right-4">
          <button
            onClick={handleSkip}
            className="text-slate-400 hover:text-slate-200 dark:text-slate-500 dark:hover:text-white transition-colors p-2"
            aria-label="Skip tour"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="text-center text-slate-900 dark:text-white max-w-lg px-6">
          {/* Animated words */}
          <div className="mb-8 h-20 flex items-center justify-center overflow-hidden">
            <div className="animate-pulse text-6xl font-bold bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
              Ready?
            </div>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
            Ready to read <span className="text-primary">3x faster</span> in 60 seconds?
          </h2>
          
          <p className="text-xl text-slate-600 dark:text-slate-300 mb-8">
            Take the Flash-Read challenge and unlock your reading potential
          </p>
          
          <button
            onClick={() => {
              startTour();
              // Move to the first tour step (demo)
              setTimeout(() => nextStep(), 50);
            }}
            className="group inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-full hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1 animate-pulse"
          >
            <Play className="w-5 h-5 mr-2" />
            Take the Challenge
          </button>
          
          <button
            onClick={handleSkip}
            className="block mx-auto mt-4 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors text-sm"
          >
            Skip for now
          </button>
        </div>
      </div>
    );
  }

  // Render demo step - minimal: only "Watch This" and reading box
  if (state.currentStep === 'demo') {
    return (
      <div className="fixed inset-0 bg-white dark:bg-slate-950 flex items-center justify-center z-50">
        <div className="absolute top-4 right-4">
          <button onClick={handleSkip} className="text-slate-400 dark:text-slate-500 hover:text-slate-200 dark:hover:text-white p-2">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="text-center text-slate-900 dark:text-white max-w-2xl px-6 w-full">
          <h2 className="text-3xl font-bold mb-6">Watch This</h2>
          
          <DemoReader 
            isActive={true} 
            wpm={200} 
            text="See how each word appears one at a time. Your brain processes text faster this way."
            onComplete={handleDemoComplete}
          />
        </div>
      </div>
    );
  }

  // Render speed explanation step with animated comparison visualization
  if (state.currentStep === 'speed-explanation') {
    const traditionalWpm = speedExplanationAnimated ? 200 : 0;
    const speedWpm = speedExplanationAnimated ? 600 : 0;
    
    return (
      <div className="fixed inset-0 bg-white dark:bg-slate-950 flex items-center justify-center z-50">
        <div className="absolute top-4 right-4">
          <button onClick={handleSkip} className="text-slate-400 dark:text-slate-500 hover:text-slate-200 dark:hover:text-white p-2">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="text-center text-slate-900 dark:text-white max-w-2xl px-6">
          <h2 className="text-2xl font-bold mb-8">Speed Reading Basics</h2>
          
          {/* Animated Speed Comparison Bars */}
          <div className="mb-10">
            {/* Your Starting Speed */}
            <div className="mb-6">
              <div className="flex justify-between items-baseline mb-2">
                <span className="text-slate-600 dark:text-slate-400 text-sm font-medium">Your Starting Speed</span>
                <span className="text-2xl font-bold text-slate-500 dark:text-slate-400">
                  {traditionalWpm} WPM
                </span>
              </div>
              <div className="h-4 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-slate-400 dark:bg-slate-500 rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${(traditionalWpm / 600) * 100}%` }}
                />
              </div>
            </div>
            
            {/* With FlashRead */}
            <div>
              <div className="flex justify-between items-baseline mb-2">
                <span className="text-primary text-sm font-medium">With Flash-Read</span>
                <span className="text-3xl font-bold text-primary dark:text-blue-400">
                  {speedWpm} WPM
                </span>
              </div>
              <div className="h-4 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${(speedWpm / 600) * 100}%` }}
                />
              </div>
            </div>
          </div>
          
          {/* Key Insight with emphasis */}
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-2xl blur-xl" />
            <div className="relative p-6 bg-slate-100 dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-slate-700">
              <p className="text-xl text-slate-700 dark:text-slate-200 mb-4">
                <span className="text-slate-600 dark:text-slate-400">200 WPM is the average reading speed.</span> With Flash-Read, you can reach <span className="text-purple-600 dark:text-purple-400 font-bold">600 WPM</span> comfortably — and up to <span className="text-purple-600 dark:text-purple-400 font-bold">2,000 WPM</span> with practice
              </p>
              
              {/* Stats breakdown */}
              <div className="grid grid-cols-3 gap-4 text-center pt-4 border-t border-slate-200 dark:border-slate-700">
                <div>
                  <div className="text-2xl font-bold text-primary dark:text-blue-400">27</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400">Books/year at 200 WPM</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-primary dark:text-blue-400">82</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400">Books/year at 600 WPM</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-primary dark:text-blue-400">55+</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400">Extra books/year</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Continue Button */}
          <button
            onClick={() => nextStep()}
            className="mt-8 inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-full hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            Continue the Challenge
            <ArrowRight className="w-5 h-5 ml-2" />
          </button>
        </div>
      </div>
    );
  }

  // Render speed ramp step - auto-ramps from 300 to 600 WPM over 15 seconds (no user controls)
  if (state.currentStep === 'speed-ramp') {
    return (
      <div className="fixed inset-0 bg-white dark:bg-slate-950 flex items-center justify-center z-50">
        <div className="absolute top-4 right-4">
          <button onClick={handleSkip} className="text-slate-400 dark:text-slate-500 hover:text-slate-200 dark:hover:text-white p-2">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="text-center text-slate-900 dark:text-white max-w-2xl px-6 w-full">
          <div className="flex items-center justify-center mb-4">
            <Zap className="w-8 h-8 text-amber-500 dark:text-amber-400 mr-2" />
            <h2 className="text-2xl font-bold">Watch Your Brain Adapt</h2>
          </div>
          
          <p className="text-slate-600 dark:text-slate-400 mb-8">Watching it ramp to 600 WPM. The app goes up to 2,000 WPM — most users settle at 400-700.</p>
          
          {/* Use DemoReader for both countdown and reading - keeps layout consistent */}
          <DemoReader 
            isActive={countdownComplete || !!countdownWord} 
            wpm={demoWpm} 
            text="Your brain adapts faster than you think. The quicker you read, the better your comprehension becomes. This is the power of RSVP speed reading training. Watch as your speed increases and your understanding stays strong. With practice you will read faster and comprehend more. Speed reading transforms how you process information. Every day your skills improve with consistent practice. Train your brain to recognize words instantly without subvocalization. The more you practice, the easier it becomes to read at higher speeds while maintaining comprehension. Keep reading and feel your abilities growing stronger each moment. The words flow more smoothly now. Your eyes dance across the text without hesitation. Focus sharpens as speed increases. This is the speed reading zone. Mastery comes with dedication. You are becoming a faster reader right now."
            onComplete={handleDemoComplete}
            countdownWord={countdownWord ? (countdownWord === 'getReady' ? 'Get Ready' : countdownWord === 'set' ? 'Set' : 'Go') : null}
          />
        </div>
      </div>
    );
  }

  // Render progress tracking step - spruced up with stats and achievements
  if (state.currentStep === 'progress-tracking') {
    return (
      <div className="fixed inset-0 bg-white dark:bg-slate-950 flex items-center justify-center z-50">
        <div className="absolute top-4 right-4">
          <button onClick={handleSkip} className="text-slate-400 dark:text-slate-500 hover:text-slate-200 dark:hover:text-white p-2">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="text-center text-slate-900 dark:text-white max-w-2xl px-6 w-full">
          {/* Header with animated icon */}
          <div className="flex items-center justify-center mb-6">
            <div className="relative">
              <Gauge className="w-10 h-10 text-emerald-500 dark:text-emerald-400" />
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full animate-ping" />
            </div>
          </div>
          
          <h2 className="text-3xl font-bold mb-2">Your Progress So Far</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 mb-8">You've completed 2 of 3 steps - almost there!</p>
          
          {/* Main Progress Circle */}
          <div className="relative w-40 h-40 mx-auto mb-8">
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
              <circle
                cx="50" cy="50" r="45"
                fill="none"
                stroke="currentColor"
                strokeWidth="8"
                className="text-slate-200 dark:text-slate-700"
              />
              <circle
                cx="50" cy="50" r="45"
                fill="none"
                stroke="currentColor"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${progressTrackingProgress * 2.83} 283`}
                className="text-emerald-500 dark:text-emerald-400 transition-all duration-500"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <div>
                <div className="text-4xl font-bold text-emerald-600 dark:text-emerald-400">{progressTrackingProgress}%</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Complete</div>
              </div>
            </div>
          </div>
          
          {/* Stats Cards Grid */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-2xl p-4 border border-blue-200 dark:border-blue-700">
              <div className="text-3xl mb-1">📖</div>
              <div className="text-xs text-blue-600 dark:text-blue-400 font-medium">Demo Read</div>
              <div className="text-lg font-bold text-slate-900 dark:text-white">16 words</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">at 200 WPM</div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 rounded-2xl p-4 border border-purple-200 dark:border-purple-700">
              <div className="text-3xl mb-1">⚡</div>
              <div className="text-xs text-purple-600 dark:text-purple-400 font-medium">Ramp Read</div>
              <div className="text-lg font-bold text-slate-900 dark:text-white">~55 words</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">200→600 WPM</div>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/30 dark:to-emerald-800/30 rounded-2xl p-4 border border-emerald-200 dark:border-emerald-700">
              <div className="text-3xl mb-1">🎯</div>
              <div className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Challenge</div>
              <div className="text-lg font-bold text-slate-900 dark:text-white">Try now!</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">Read at 600 WPM</div>
            </div>
          </div>
          
          {/* Achievement Preview */}
          <div className="relative mb-8">
            <div className="absolute -inset-2 bg-gradient-to-r from-emerald-500/20 via-blue-500/20 to-purple-500/20 rounded-2xl blur-lg" />
            <div className="relative bg-slate-100 dark:bg-slate-800 rounded-xl p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center text-2xl">
                🏆
              </div>
              <div className="text-left">
                <div className="font-bold text-slate-900 dark:text-white">Speed Reader Badge</div>
                <div className="text-sm text-slate-600 dark:text-slate-400">Complete the challenge to unlock!</div>
              </div>
            </div>
          </div>
          
          {/* Motivational message */}
          <p className="text-lg font-medium text-primary dark:text-blue-400 mb-6">
            🎯 Final challenge awaits - prove yourself as a speed reader!
          </p>
          
          {/* Start Challenge Button */}
          <button
            onClick={() => nextStep()}
            className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-full hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            Continue: The Challenge
            <ArrowRight className="w-5 h-5 ml-2" />
          </button>
        </div>
      </div>
    );
  }

  // Render summary step with results
  if (state.currentStep === 'summary') {
    const totalWords = 16 + 55 + readingStats.challengeWordsRead;
    const traditionalTime = (totalWords / 200) * 60 * 1000; // 200 WPM typical
    const speedTime = (totalWords / 600) * 60 * 1000; // 600 WPM
    const timeSaved = traditionalTime - speedTime;
    
    return (
      <div className="fixed inset-0 bg-white dark:bg-slate-950 flex items-center justify-center z-50">
        <div className="absolute top-4 right-4">
          <button onClick={handleSkip} className="text-slate-400 dark:text-slate-500 hover:text-slate-200 dark:hover:text-white p-2">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="text-center text-slate-900 dark:text-white max-w-2xl px-6 w-full">
          <div className="text-6xl mb-4">📊</div>
          <h2 className="text-3xl font-bold mb-2">Your Results</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 mb-8">Here's how you did!</p>
          
          {/* Speed Progress */}
          <div className="bg-slate-100 dark:bg-slate-800 rounded-2xl p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4">Reading Speed Journey</h3>
            <div className="flex items-center justify-center gap-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-500">200</div>
                <div className="text-xs text-slate-500">WPM Start</div>
              </div>
              <ArrowRight className="w-6 h-6 text-slate-400" />
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-500">600</div>
                <div className="text-xs text-slate-500">WPM Ramp</div>
              </div>
              <ArrowRight className="w-6 h-6 text-slate-400" />
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-500">600</div>
                <div className="text-xs text-slate-500">WPM Challenge</div>
              </div>
            </div>
          </div>
          
          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-xl p-4 border border-blue-200 dark:border-blue-700">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{totalWords}</div>
              <div className="text-sm text-slate-600 dark:text-slate-400">Total Words Read</div>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/30 dark:to-emerald-800/30 rounded-xl p-4 border border-emerald-200 dark:border-emerald-700">
              <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{(timeSaved / 1000).toFixed(1)}s</div>
              <div className="text-sm text-slate-600 dark:text-slate-400">Time Saved vs Normal</div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 rounded-xl p-4 border border-purple-200 dark:border-purple-700">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">3x</div>
              <div className="text-sm text-slate-600 dark:text-slate-400">Speed Increase</div>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/30 dark:to-amber-800/30 rounded-xl p-4 border border-amber-200 dark:border-amber-700">
              <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">12+</div>
              <div className="text-sm text-slate-600 dark:text-slate-400">Extra Books/Year</div>
            </div>
          </div>
          
          {/* Continue Button */}
          <button
            onClick={handleNext}
            className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-full hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            Continue
            <ArrowRight className="w-5 h-5 ml-2" />
          </button>
        </div>
      </div>
    );
  }
  if (state.currentStep === 'challenge') {
    return (
      <div className="fixed inset-0 bg-white dark:bg-slate-950 flex items-center justify-center z-50">
        <div className="absolute top-4 right-4">
          <button onClick={handleSkip} className="text-slate-400 dark:text-slate-500 hover:text-slate-200 dark:hover:text-white p-2">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="text-center text-slate-900 dark:text-white max-w-2xl px-6 w-full">
          <h2 className="text-2xl font-bold mb-2">The Challenge</h2>
          <p className="text-slate-600 dark:text-slate-400 mb-8">Read this at 600 WPM (app supports up to 2,000!)</p>
          
          {!showChallenge ? (
            <div className="mb-8">
              <button
                onClick={() => setShowChallenge(true)}
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-full hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <Play className="w-5 h-5 mr-2" />
                Ready — Start Reading
              </button>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-4">
                Click when you're ready to begin
              </p>
              <button
                onClick={handleSkip}
                className="mt-6 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 text-sm"
              >
                Return to Home
              </button>
            </div>
          ) : (
            <div className="min-h-[320px] flex flex-col items-center justify-center">
              <DemoReader 
                isActive={showChallenge} 
                wpm={600} 
                text={CHALLENGE_TEXT}
                onComplete={handleChallengeComplete}
              />
              
              {challengeComplete && (
                <div className="mt-4 animate-bounce">
                  <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                    600 WPM achieved! 🎉
                  </p>
                  <p className="text-slate-600 dark:text-slate-300 mt-2">
                    You're now reading 3x faster than average
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Render conversion step
  if (state.currentStep === 'conversion') {
    return (
      <div className="fixed inset-0 bg-white dark:bg-slate-950 flex items-center justify-center z-50">
        <div className="absolute top-4 right-4">
          <button onClick={handleSkip} className="text-slate-400 dark:text-slate-500 hover:text-slate-200 dark:hover:text-white p-2">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="text-center text-slate-900 dark:text-white max-w-lg px-6">
          <div className="text-6xl mb-4">🎉</div>
          
          <h2 className="text-3xl font-bold mb-4">You're a Speed Reader Now!</h2>
          
          <p className="text-xl text-slate-600 dark:text-slate-300 mb-8">
            Join early readers who are discovering faster reading
          </p>
          
          <AchievementDisplay isActive={true} />
          
          <div className="mt-6 space-y-3">
            <button
              onClick={handleSkip}
              className="w-full max-w-xs mx-auto block py-3 px-5 bg-primary text-white font-semibold rounded-full hover:bg-blue-700 transition-all"
            >
              Return Home
            </button>
            
            <button
              onClick={onSignup}
              className="w-full max-w-xs mx-auto block py-3 px-5 bg-slate-200 dark:bg-slate-800 text-slate-900 dark:text-white font-semibold rounded-full hover:bg-slate-300 dark:hover:bg-slate-700 transition-all"
            >
              Sign Up to Save Progress
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render progress bar at bottom for all steps
  if (state.isActive) {
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm border-t border-slate-200 dark:border-slate-800 p-4 z-40">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-600 dark:text-slate-400 text-sm">
              Step {getStepIndex() + 1} of {totalSteps}
            </span>
            <div className="flex gap-2">
              <button
                onClick={handleSkip}
                className="px-4 py-2 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors text-sm"
              >
                Skip
              </button>
              <button
                onClick={handleNext}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center"
              >
                Next <ArrowRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary rounded-full transition-all duration-300"
              style={{ width: `${state.progress}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default OnboardingTour;
